import i18next, { type InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
// date-fns locales extended with DayPicker's calendar labels ("Go to the Next
// Month", "selected"), which stay English with plain date-fns locales.
import {
  da,
  de,
  enGB,
  es,
  fi,
  fr,
  it,
  nb,
  nl,
  pl,
  pt,
  sv,
  tr,
} from 'react-day-picker/locale';
import { api } from '@http/constants';
import { I18N_EVENTS, I18N_STORE_EVENTS } from '@/i18n/events';
import { localLocales } from '@/i18n/source';

export const languages = {
  de: {
    name: 'Deutsch',
    locale: de,
  },
  en: {
    name: 'English',
    locale: enGB,
  },
  es: {
    name: 'Español',
    locale: es,
  },
  fr: {
    name: 'Français',
    locale: fr,
  },
  it: {
    name: 'Italiano',
    locale: it,
  },
  nl: {
    name: 'Nederlands',
    locale: nl,
  },
  pl: {
    name: 'Polski',
    locale: pl,
  },
  pt: {
    name: 'Português',
    locale: pt,
  },
  da: {
    name: 'Dansk',
    locale: da,
  },
  sv: {
    name: 'Svenska',
    locale: sv,
  },
  fi: {
    name: 'Suomi',
    locale: fi,
  },
  tr: {
    name: 'Türkçe',
    locale: tr,
  },
  no: {
    name: 'Norsk',
    locale: nb,
  },
} as const;

export type SupportedLanguage = keyof typeof languages;

const codes = Object.keys(languages);

export const translationVersion =
  import.meta.env.VITE_TRANSLATION_VERSION || __APP_VERSION__;

export function normalizeSupportedLanguage(
  language?: string | null,
): SupportedLanguage {
  if (!language) return 'en';

  const normalizedLanguage = language.toLowerCase().split('-')[0];
  return normalizedLanguage in languages
    ? (normalizedLanguage as SupportedLanguage)
    : 'en';
}

const i18nOptions: InitOptions = {
  fallbackLng: 'en', // Do not remove: required fallback to English when the platform does not support the user's language.
  supportedLngs: codes,
  debug: false,
  saveMissing: true,
  missingKeyHandler: (lngs, ns, key, fallbackValue) => {
    // Handle the missing key, for example, log it to the console
    if (key === 'homepage') return; // ignore this key, it is included but throws a warning

    if (import.meta.env.VITE_HIDE_MISSING_TRANSLATION_WARNING !== 'true') {
      console.warn(
        `Missing translation for key: "${key}" in namespace: "${ns}" for languages: "${lngs.join(
          ', ',
        )}". Fallback value: "${fallbackValue}"`,
      );
    }
  },
  interpolation: {
    escapeValue: false, // not needed for react as it escapes by default
  },
  react: {
    useSuspense: false,
    bindI18n: I18N_EVENTS.join(' '),
    // React-i18next also needs store events so addResource(...) updates become visible immediately.
    bindI18nStore: I18N_STORE_EVENTS.join(' '),
  },
  backend: {
    loadPath: localLocales
      ? `${import.meta.env.BASE_URL}locales/{{lng}}/{{ns}}.json`
      : api.i18n +
        '?namespace={{ns}}&locale={{lng}}&version=' +
        translationVersion,
  },
  load: 'languageOnly', // only load main language code, not country specific
};

function syncHtmlLang(language?: string | null) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = normalizeSupportedLanguage(
    language ?? i18next.resolvedLanguage ?? i18next.language,
  );
}

i18next.on('languageChanged', syncHtmlLang);
i18next.on('initialized', () => syncHtmlLang());
i18next.on('loaded', () => syncHtmlLang());

void i18next
  // load translations over http from the API (see backend.loadPath)
  // learn more: https://github.com/i18next/i18next-http-backend
  .use(HttpBackend)
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init(i18nOptions);

export default i18next;
